import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { PlatformAdminListItem, PlatformOrganizationListItem } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { PlatformTabs } from '../components/PlatformTabs';

interface ClubAdminRow {
  userId: string;
  email: string;
  status: string;
  organizationId: string;
  organizationName: string;
}

export function PlatformClubAdminsPage() {
  const qc = useQueryClient();
  const { data: admins } = useQuery({
    queryKey: ['platform', 'admins'],
    queryFn: () => api<PlatformAdminListItem[]>('/platform/admins'),
  });
  const { data: orgs } = useQuery({
    queryKey: ['platform', 'orgs'],
    queryFn: () => api<PlatformOrganizationListItem[]>('/platform/organizations'),
  });

  const rows: ClubAdminRow[] = (admins ?? [])
    .flatMap((a) =>
      a.clubAdminOrgs.map((org) => ({
        userId: a.userId,
        email: a.email,
        status: a.status,
        organizationId: org.organizationId,
        organizationName: org.organizationName,
      })),
    )
    .sort((x, y) => x.organizationName.localeCompare(y.organizationName) || x.email.localeCompare(y.email));

  const [orgId, setOrgId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [manualPassword, setManualPassword] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Record<string, string>>({});

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
    void qc.invalidateQueries({ queryKey: ['platform', 'orgs'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api(`/platform/organizations/${orgId}/admins`, {
        method: 'POST',
        body: {
          email,
          password: password || undefined,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
        },
      }),
    onSuccess: () => {
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      invalidate();
    },
    onError: (e) => setCreateError(e instanceof ApiError ? e.message : 'Fehlgeschlagen.'),
  });

  const rowKey = (r: ClubAdminRow) => `${r.organizationId}:${r.userId}`;

  const remove = useMutation({
    mutationFn: (r: ClubAdminRow) => api(`/platform/organizations/${r.organizationId}/admins/${r.userId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (e, r) => setResult((m) => ({ ...m, [rowKey(r)]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });

  const resetEmail = useMutation({
    mutationFn: (r: ClubAdminRow) =>
      api(`/platform/admins/${r.userId}/reset-password`, {
        method: 'POST',
        // routes through this org's own SMTP override if it has one
        // configured (Verein › SMTP Server), else the platform default
        body: { mode: 'email', organizationId: r.organizationId },
      }),
    onSuccess: (_r, r) => setResult((m) => ({ ...m, [rowKey(r)]: 'E-Mail zum Zurücksetzen verschickt.' })),
    onError: (e, r) => setResult((m) => ({ ...m, [rowKey(r)]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });
  const resetManual = useMutation({
    mutationFn: ({ r, password: p }: { r: ClubAdminRow; password: string }) =>
      api(`/platform/admins/${r.userId}/reset-password`, { method: 'POST', body: { mode: 'manual', password: p } }),
    onSuccess: (_res, { r }) => {
      setManualPassword((m) => ({ ...m, [rowKey(r)]: '' }));
      setResult((m) => ({ ...m, [rowKey(r)]: 'Neues Passwort gesetzt.' }));
    },
    onError: (e, { r }) => setResult((m) => ({ ...m, [rowKey(r)]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">Vereinsadmins</h1>

      <Card title="Vereinsadmin anlegen">
        {createError && <Alert>{createError}</Alert>}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setCreateError(null);
            create.mutate();
          }}
        >
          <Field label="Verein">
            <select
              className="w-48 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
            >
              <option value="">Verein wählen…</option>
              {orgs?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Vorname">
            <Input className="w-32" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </Field>
          <Field label="Nachname">
            <Input className="w-32" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </Field>
          <Field label="E-Mail">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Passwort (optional)">
            <Input
              type="text"
              placeholder="mind. 10 Zeichen"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={!orgId || !firstName.trim() || !lastName.trim() || create.isPending}>
            Anlegen
          </Button>
        </form>
        <p className="mt-2 text-xs text-slate-400">
          Legt automatisch auch einen Mitgliedsdatensatz in diesem Verein an — ein Vereinsadmin
          steht damit immer selbst in der Mitgliederliste und kann z. B. auf Trainingstermine
          antworten. Ohne Passwort wird eine Einladung per E-Mail verschickt.
        </p>
      </Card>

      <Card title={`${rows.length} Vereinsadmins`}>
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={rowKey(r)} className="py-3">
              {/* Gleiche Zwei-Zeilen-Kompaktierung wie PlatformAdminsPage
               * (§ Nutzeranfrage „restliche Seiten … in der mobilen Ansicht
               * anpassen") — siehe dort für die Begründung. */}
              <div className="min-w-0">
                <div className="truncate font-medium">{r.email}</div>
                <div className="truncate text-xs text-slate-500">
                  {r.organizationName} · {r.status}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={() => {
                    setResult((m) => ({ ...m, [rowKey(r)]: '' }));
                    resetEmail.mutate(r);
                  }}
                  disabled={resetEmail.isPending}
                >
                  Passwort per E-Mail zurücksetzen
                </Button>
                <Button
                  variant="danger"
                  className="px-2 py-1 text-xs"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm(`„${r.email}" als Vereinsadmin von „${r.organizationName}" entfernen?`)) {
                      setResult((m) => ({ ...m, [rowKey(r)]: '' }));
                      remove.mutate(r);
                    }
                  }}
                >
                  Entfernen
                </Button>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Input
                  type="text"
                  placeholder="neues Passwort (mind. 10 Zeichen)"
                  className="w-full min-w-0 flex-1 px-2.5 py-1.5 text-xs sm:w-56 sm:flex-none"
                  value={manualPassword[rowKey(r)] ?? ''}
                  onChange={(e) => setManualPassword((m) => ({ ...m, [rowKey(r)]: e.target.value }))}
                />
                <Button
                  variant="ghost"
                  className="shrink-0 px-2 py-1 text-xs"
                  disabled={(manualPassword[rowKey(r)]?.length ?? 0) < 10 || resetManual.isPending}
                  onClick={() => {
                    setResult((m) => ({ ...m, [rowKey(r)]: '' }));
                    resetManual.mutate({ r, password: manualPassword[rowKey(r)]! });
                  }}
                >
                  manuell setzen
                </Button>
              </div>
              {result[rowKey(r)] && <p className="mt-1.5 text-xs text-slate-500">{result[rowKey(r)]}</p>}
            </div>
          ))}
          {rows.length === 0 && <p className="py-2 text-sm text-slate-500">Keine Vereinsadmins.</p>}
        </div>
      </Card>
    </div>
  );
}
